import { PayloadAction } from '@reduxjs/toolkit';
import {
  all,
  call,
  put,
  takeLatest,
  take,
} from 'redux-saga/effects';
import { eventChannel, EventChannel } from 'redux-saga';
import { aiTextGenerationApi } from '../api/ai-text-generation-api';
import {
  generateTextRequest,
  generateTextChunk,
  generateTextSuccess,
  generateTextFailure,
  generateTextConsentRequired,
} from './ai-text-generation-slice';
import { esConsentimientoRequerido } from '@/features/ai-consent/consent-required';
import { TextGenerationRequest } from '../types/ai-text-generation.types';

/**
 * Helper para obtener mensajes de error
 */
const getErrorMessage = (error: unknown): string => {
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as any;
    const response = axiosError.response;

    // Rate limit detection
    if (response?.status === 429) {
      return 'Has alcanzado el limite de solicitudes. Intenta de nuevo en unos minutos.';
    }
    if (response?.status === 403 && response?.data?.message?.toLowerCase?.().includes('limit')) {
      return response.data.message;
    }

    if (response?.data) {
      if (typeof response.data === 'string') {
        return response.data;
      }
      if (response.data.message) {
        return response.data.message;
      }
      if (response.data.errors && Array.isArray(response.data.errors)) {
        return response.data.errors
          .map((e: any) => `${e.field || ''}: ${e.message || e}`)
          .join(', ');
      }
    }

    return `Error ${response?.status || 'desconocido'}: ${response?.statusText || 'Error en la peticion'}`;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'Ha ocurrido un error inesperado al generar el texto';
};

/**
 * Crear canal de eventos para streaming de texto
 */
function createTextStreamChannel(request: TextGenerationRequest): EventChannel<string> {
  return eventChannel((emitter) => {
    let fullText = '';
    
    aiTextGenerationApi.generateText(request, (chunk: string) => {
      fullText += chunk;
      emitter(chunk);
    })
      .then((response) => {
        // Si no llegó ningún chunk (el respaldo por POST devuelve el texto entero), se entrega ese texto. Antes se
        // ignoraba `response.text` y la generación terminaba vacía, sin aviso (medido en prod, 16-oct 14:52Z).
        if (!fullText && response?.text) {
          fullText = response.text;
          emitter(response.text);
        }
        emitter('__COMPLETE__');
      })
      .catch((error) => {
        // PMO #23381: sin consentimiento no es un error de texto: la UI abre el modal y reintenta.
        emitter(
          esConsentimientoRequerido(error)
            ? '__CONSENT__'
            : `__ERROR__:${error.message || 'Error desconocido'}`,
        );
      });

    return () => {
      // Cleanup
    };
  });
}

/**
 * Saga para generar texto con IA con streaming
 */
function* generateTextSaga(action: PayloadAction<TextGenerationRequest>) {
  try {
    const channel: EventChannel<string> = yield call(createTextStreamChannel, action.payload);
    let fullText = '';
    let hasStartedStreaming = false;

    try {
      while (true) {
        const chunk: string = yield take(channel);
        
        if (chunk === '__COMPLETE__') {
          // Streaming completo
          yield put(generateTextSuccess({
            text: fullText,
            style: action.payload.style,
            metadata: {
              model: 'gpt-4',
              provider: 'openai',
            },
          }));
          break;
        } else if (chunk === '__CONSENT__') {
          yield put(generateTextConsentRequired());
          break;
        } else if (chunk.startsWith('__ERROR__:')) {
          // Error durante streaming
          const errorMessage = chunk.replace('__ERROR__:', '');
          yield put(generateTextFailure(errorMessage));
          break;
        } else {
          // Chunk de texto recibido
          if (!hasStartedStreaming) {
            hasStartedStreaming = true;
          }
          fullText += chunk;
          yield put(generateTextChunk(chunk));
        }
      }
    } finally {
      channel.close();
    }
  } catch (error) {
    yield put(generateTextFailure(getErrorMessage(error)));
  }
}

/**
 * Saga principal de generación de texto con IA
 */
export function* aiTextGenerationSaga() {
  yield all([
    takeLatest(generateTextRequest.type, generateTextSaga),
  ]);
}

